import { apiFetch } from "../http-client";
import { useContextStore, type User } from "@/stores/context";
import { isValidAvatarHash, isValidDiscordId } from "./errorHandler";

const VITE_API_URL = import.meta.env.VITE_API_URL || "";

/**
 * The primary authentication function for the application.
 * It checks for a valid session on the backend. If one exists, it fetches
 * the user's full details (including admin level) and populates the Pinia store.
 * This should be called by your router guards.
 *
 * @param {boolean} [force=false] - If true, bypasses the local store check and forces a network request.
 * @returns {Promise<boolean>} - Resolves to true if the user is logged in, false otherwise.
 */
export async function checkLoginStatus(
  force: boolean = false
): Promise<boolean> {
  const contextStore = useContextStore();

  if (contextStore.isLoggedIn && !force) {
    return true;
  }

  try {
    const response = await apiFetch(`${VITE_API_URL}/api/auth/user/info`, {
      method: "GET",
      allow401: true as any,
    } as any);

    if (response.status === 401) {
      contextStore.clearUser();
      return false;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch user data: ${response.statusText}`);
    }

    // MODIFIED: The backend now sends the complete session user object
    const userData = await response.json();

    // ✅ SECURITY: Validate Discord ID and avatar hash format before storing
    if (!isValidDiscordId(userData.id)) {
      throw new Error("Invalid Discord user ID format received from server");
    }

    // MODIFIED: Map the new backend response to the User interface in the Pinia store.
    const userForStore: User = {
      id: userData.id,
      username: userData.username,
      avatar: userData.avatar && isValidAvatarHash(userData.avatar) 
        ? userData.avatar 
        : null, // ✅ SECURITY: Validate avatar hash or set to null
      adminLevel: userData.adminLevel || null, // The crucial change: use adminLevel and default to null.
    };

    contextStore.setUser(userForStore); // <-- Populates the store with the correct data structure

    return true;
  } catch (error) {
    console.error("Error during login status check:", error);
    contextStore.clearUser();
    return false;
  }
}

/**
 * Builds the URL for initiating the Discord OAuth2 login flow.
 */
export async function DiscordAuthenticator(): Promise<string> {
  return `${VITE_API_URL}/api/auth/discord`;
}

/**
 * Calls the backend to destroy the user's session and log them out.
 */
export async function DiscordAuthenticatorLogout(): Promise<void> {
  try {
    const response = await apiFetch(`${VITE_API_URL}/api/auth/logout`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`Failed to log out: ${response.statusText}`);
    }
  } catch (error) {
    console.error("Error during Discord logout:", error);
    throw error;
  }
}

/**
 * Legacy function for fetching user data.
 * It's recommended to rely on the `contextStore.user` object after `checkLoginStatus` has run.
 * However, it can still be used if you need to manually re-fetch user data.
 * @param {boolean} [force=false] - If true, forces a network request.
 */
export async function getDiscordUserByCrededentials(force: boolean = false) {
  const success = await checkLoginStatus(force);
  if (success) {
    const contextStore = useContextStore();
    const user = contextStore.user;

    if (!user) return null;

    // ✅ SECURITY: Reconstruct avatarUrl with validated hash only
    const avatarUrl = user.avatar && isValidAvatarHash(user.avatar)
      ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`
      : `https://cdn.discordapp.com/embed/avatars/${
          parseInt(user.id.slice(-1)) % 5
        }.png`;

    return {
      username: user.username,
      avatarUrl: avatarUrl,
    };
  }
  return null;
}
